import SharedDialog from './shared/shared-dialog';
import { pagesConfig } from './shared/pages-config';
import { Link as ReactRouterLink } from 'react-router-dom';
import { getSessionTimeout, UserKeys } from './api/users-api';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface SessionTimeoutDialogProps {
  open: boolean;
  handleClose: () => void;
  lastAccessTime: number;
  sessionTimeout: number;
  dataUpdatedAt: number;
}

export default function SessionTimeoutDialog({
  open,
  handleClose,
  lastAccessTime,
  sessionTimeout,
  dataUpdatedAt,
}: SessionTimeoutDialogProps) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  function calculateRemainingMinutes() {
    const now = new Date().getTime();
    const difference = now - lastAccessTime;
    const minutesPassed = Math.floor(difference / (1000 * 60));
    return sessionTimeout - minutesPassed;
  }

  async function handleStayLoggedIn() {
    try {
      setIsLoading(true);
      await getSessionTimeout();
      queryClient.invalidateQueries({ queryKey: [UserKeys.UserSettings] });
      setIsLoading(false);
      handleClose();
    } catch {
      setIsLoading(false);
      handleClose();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Session Timeout`}
      dialogContent={
        <Stack
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
          gap={2}
          key={dataUpdatedAt}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Your session will expire in {calculateRemainingMinutes()} minute
            {calculateRemainingMinutes() !== 1 ? 's' : ''}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Stay Logged In" to extend your session and continue using
            gPanel. You can modify the session timeout duration{' '}
            <Link
              component={ReactRouterLink}
              to={`/${pagesConfig.settings.link}/${pagesConfig.sessionTimeout.link}`}
              rel="noopener noreferrer"
              onClick={handleClose}
              sx={{ color: theme.palette.semantic.hyperlink.active }}
            >
              here
            </Link>
            .
          </Typography>
        </Stack>
      }
      noCancel
      primaryButton={{
        title: 'Stay Logged In',
        action: handleStayLoggedIn,
        loading: isLoading,
        disabled: isLoading,
      }}
    />
  );
}
